import { Button } from '@deepguide-ai/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog';
import { Input } from '@deepguide-ai/ui/input';
import { Label } from '@deepguide-ai/ui/label';
import { queryClient, trpc, trpcClient } from '@/lib/trpc';
import { FC, useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { ConfigModalProps } from '../types';
import { IntegrationFeature, IntegrationProvider } from '../types';

const DrataIconWrapper: FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="24" height="24" rx="4" fill="#0066FF"/>
    <circle cx="12" cy="8" r="2" fill="white"/>
    <circle cx="8" cy="16" r="2" fill="white"/>
    <circle cx="16" cy="16" r="2" fill="white"/>
    <path
      d="M12 10L8 14M12 10L16 14"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * RFC-085/RFC-086: Drata configuration modal
 * Allows users to configure API credentials for Drata compliance automation
 */
const DrataConfigModal: FC<ConfigModalProps> = ({
  open,
  onOpenChange,
  integration,
  onComplete,
  workspaceId,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [drataWorkspaceId, setDrataWorkspaceId] = useState('');
  const [region, setRegion] = useState<'us' | 'eu' | 'apac'>('us');
  const [ownerId, setOwnerId] = useState('');

  useEffect(() => {
    if (integration && open) {
      const config = integration.config as any;
      setApiKey(config?.apiKey || '');
      setDrataWorkspaceId(config?.workspaceId?.toString() || '');
      setRegion(config?.region || 'us');
      setOwnerId(config?.ownerId?.toString() || '');
    }
  }, [integration, open]);

  const [isSaving, setIsSaving] = useState(false);

  const onSave = async () => {
    if (!apiKey.trim()) {
      toast.error('API Key is required');
      return;
    }

    if (!drataWorkspaceId.trim()) {
      toast.error('Drata Workspace ID is required');
      return;
    }

    const parsedWorkspaceId = parseInt(drataWorkspaceId.trim(), 10);
    if (isNaN(parsedWorkspaceId) || parsedWorkspaceId <= 0) {
      toast.error('Drata Workspace ID must be a valid positive number');
      return;
    }

    setIsSaving(true);
    try {
      const config = {
        apiKey: apiKey.trim(),
        workspaceId: parsedWorkspaceId,
        region: region,
        ...(ownerId.trim() && { ownerId: parseInt(ownerId.trim(), 10) })
      };

      if (integration) {
        // Update existing integration metadata (config is stored separately)
        await trpcClient.integrations.updateIntegration.mutate({
          id: integration.id,
          metadata: {
            ...integration.metadata,
            drataConfig: config,
          },
        });
        toast.success('Drata integration updated successfully');
      }

      queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
      onOpenChange(false);
      onComplete();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save Drata integration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Configure Drata Integration</DialogTitle>
          <DialogDescription>
            Connect your Drata account to automatically upload compliance evidence packs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="apiKey">
              API Key <span className="text-destructive">*</span>
            </Label>
            <Input
              id="apiKey"
              type="password"
              placeholder="Enter your Drata API key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              You can find your API key in Drata Settings → Integrations → API Tokens
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="drataWorkspaceId">
              Drata Workspace ID <span className="text-destructive">*</span>
            </Label>
            <Input
              id="drataWorkspaceId"
              type="number"
              placeholder="Enter your Drata Workspace ID (e.g., 12345)"
              value={drataWorkspaceId}
              onChange={(e) => setDrataWorkspaceId(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Find your Workspace ID in the Drata URL: https://app.drata.com/workspaces/[ID]
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="region">Region</Label>
            <select
              id="region"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={region}
              onChange={(e) => setRegion(e.target.value as 'us' | 'eu' | 'apac')}
            >
              <option value="us">North America (US)</option>
              <option value="eu">Europe (EU)</option>
              <option value="apac">Asia-Pacific (APAC)</option>
            </select>
            <p className="text-sm text-muted-foreground">
              Select the region where your Drata instance is hosted
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ownerId">Evidence Owner ID (Optional)</Label>
            <Input
              id="ownerId"
              type="number"
              placeholder="Enter Drata user ID for evidence ownership"
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Optional: User ID from Drata to assign as evidence owner
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-4">
            <p className="text-sm text-status-blue-strong">
              <strong>Evidence Library V2:</strong> Evidence will be automatically uploaded to
              Drata's Evidence Library with one-year renewal schedule.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Drata Integration Provider
 * Manages Drata compliance automation integration
 */
export const DrataProvider: IntegrationProvider = {
  id: 'Drata',
  name: 'Drata',
  description: 'Automated compliance evidence uploads to Drata platform',
  Icon: DrataIconWrapper,
  ConfigModal: DrataConfigModal,

  getFeatures: (): IntegrationFeature[] => [
    {
      id: 'compliance_upload',
      name: 'Compliance Upload',
      description: 'Automatically upload SOC 2 evidence packs to Drata',
      enabled: true,
    },
    {
      id: 'scheduled_evidence',
      name: 'Scheduled Evidence Generation',
      description: 'Generate and upload evidence on a schedule',
      enabled: true,
    },
  ],

  handleConnect: async ({ workspaceId, onSuccess }) => {
    // Drata uses API key authentication - create integration record first
    // Configuration is done through the modal
    try {
      const integration = await trpcClient.integrations.create.mutate({
        workspaceId,
        type: 'Drata',
        name: 'Drata',
      });

      // Call onSuccess to trigger modal opening
      onSuccess?.(integration);

      return { success: true };
    } catch (error) {
      console.error('Failed to create Drata integration:', error);
      return { success: false, error };
    }
  },

  syncData: async (_integration) => {
    // No data sync needed for Drata - it's push-only (we upload to them)
    console.log('Drata integration does not require data sync (push-only)');
  },
};
